import { createRouter, createWebHistory } from "vue-router";
import { isSignedIn } from "../auth";
import { useSession } from "../stores/session";

const router = createRouter({
  history: createWebHistory(),
  // Only Add Knowledge needs a forced reset: it's a long single-column form whose Title field
  // (and the reason Submit is disabled) live at the top, so landing anywhere else hides them.
  // Every other route keeps today's browser-default behavior (no adjustment).
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.name === "upload") return { top: 0 };
    return false;
  },
  routes: [
    { path: "/", redirect: "/chat" },
    // Public pages render without the app shell (no nav, no session).
    { path: "/signin", name: "signin", component: () => import("../views/SignInView.vue"), meta: { public: true } },
    { path: "/signed-out", name: "signedOut", component: () => import("../views/SignedOutView.vue"), meta: { public: true } },
    // Landing page for the dev-only lockdown gate: any signed-in caller whose email isn't in
    // CORTEX_ALLOWED_EMAILS on dev is bounced here by the guard below (prod's backend always
    // returns dev_allowed=true, so the guard never fires and this route is unreachable there).
    // Marked public so the guard's redirect isn't itself gated on being signed in.
    { path: "/dev-blocked", name: "devBlocked", component: () => import("../views/DevBlockedView.vue"), meta: { public: true } },
    { path: "/chat/:id?", name: "chat", component: () => import("../views/ChatView.vue") },
    { path: "/about", name: "about", component: () => import("../views/AboutView.vue") },
    // Admin views are lazily loaded, so a Velma User never downloads them.
    { path: "/admin/dashboard", name: "dashboard", component: () => import("../views/DashboardView.vue"), meta: { admin: true } },
    { path: "/admin/audit-log", name: "auditLog", component: () => import("../views/AuditLogView.vue"), meta: { admin: true } },
    { path: "/admin/roi-estimate", name: "roiEstimate", component: () => import("../views/RoiEstimateView.vue"), meta: { admin: true } },
    { path: "/admin/assigned", name: "assigned", component: () => import("../views/AssignedWorkView.vue"), meta: { admin: true, managerAllowed: true } },
    { path: "/admin/filled-gaps", name: "filledGaps", component: () => import("../views/FilledGapsView.vue"), meta: { admin: true, managerAllowed: true } },
    { path: "/admin/questions", name: "questionFeed", component: () => import("../views/QuestionFeedView.vue"), meta: { admin: true, managerAllowed: true } },
    { path: "/admin/insights", name: "topicInsights", component: () => import("../views/TopicInsightsView.vue"), meta: { admin: true, managerAllowed: true } },
    { path: "/admin/log", name: "log", component: () => import("../views/LogView.vue"), meta: { admin: true } },
    { path: "/admin/query-tags", name: "queryTags", component: () => import("../views/QueryTagsView.vue"), meta: { admin: true } },
    { path: "/admin/gaps", name: "gaps", component: () => import("../views/GapsView.vue"), meta: { admin: true } },
    { path: "/admin/out-of-scope", name: "outOfScope", component: () => import("../views/OutOfScopeView.vue"), meta: { admin: true } },
    { path: "/admin/atlassian-data", name: "atlassianData", component: () => import("../views/AtlassianDataView.vue"), meta: { admin: true } },
    { path: "/admin/egnyte-data", name: "egnyteData", component: () => import("../views/EgnyteDataView.vue"), meta: { admin: true, managerAllowed: true } },
    { path: "/admin/feedback", name: "feedback", component: () => import("../views/FeedbackView.vue"), meta: { admin: true } },
    // Submitting is open to every Velma User; deciding on those submissions is Admin-only.
    { path: "/admin/upload", name: "upload", component: () => import("../views/UploadView.vue") },
    { path: "/admin/addition-requests", name: "additionRequests", component: () => import("../views/AdditionRequestsView.vue"), meta: { admin: true } },
    // Search & request is open to every Velma User; deciding on those requests is Admin-only.
    { path: "/admin/delete-knowledge", name: "deleteKnowledge", component: () => import("../views/DeleteKnowledgeView.vue") },
    { path: "/admin/removal-requests", name: "removalRequests", component: () => import("../views/RemovalRequestsView.vue"), meta: { admin: true } },
    { path: "/admin/people", name: "people", component: () => import("../views/UsersView.vue"), meta: { admin: true } },
    { path: "/admin/mcp", name: "mcp", component: () => import("../views/McpView.vue"), meta: { admin: true } },
  ],
});

router.beforeEach(async (to) => {
  if (to.meta.public) {
    // Someone already signed in has no business on the sign-in page.
    if (to.name === "signin" && (await isSignedIn())) return { name: "chat" };
    return true;
  }

  // Everything with a shell requires a sign-in; the guard is what sends people to the front
  // door instead of auto-redirecting them to Entra mid-request.
  if (!(await isSignedIn())) {
    return to.fullPath === "/chat"
      ? { name: "signin" }
      : { name: "signin", query: { redirect: to.fullPath } };
  }

  const session = useSession();
  // Kicked off here (idempotent). The load is awaited below before the dev-lockdown check
  // because the SPA must know dev_allowed before letting the target view mount -- otherwise
  // an un-allowlisted dev user would flash the app briefly before /api/me resolved. On the
  // first-ever navigation of a session this adds one /api/me round-trip to the initial page
  // load; every subsequent nav gets the already-resolved cached promise back and is instant.
  const ready = session.load();

  // Dev-only lockdown gate: on dev with CORTEX_ALLOWED_EMAILS set, /api/me returns
  // dev_allowed=false for non-tester emails and the SPA sends them to /dev-blocked so they
  // see a "please use production" page instead of a working app. Prod's backend always
  // returns dev_allowed=true (the env var is unset there), so session.devAllowed is true,
  // this whole block is a false branch, and routing is byte-identical to today. Awaited so
  // the redirect happens before the target view mounts and fires off its own API calls (any
  // of which the middleware would 403 anyway, but the UI would flash content first).
  await ready;
  if (!session.devAllowed && to.name !== "devBlocked") {
    return { name: "devBlocked" };
  }

  // A convenience, not a control: it keeps a non-admin from landing on a page of 403s. The API
  // enforces the real thing.
  if (!to.meta.admin) return true;
  if (session.isAdmin) return true;
  // Managers get the routes flagged with managerAllowed; every other admin URL bounces to their
  // default landing page (Manager Inbox). Using a meta flag rather than an allow-list here so
  // adding a future manager-visible route just needs the flag on that route -- no guard edit.
  if (session.isManager) {
    return to.meta.managerAllowed === true ? true : { name: "assigned" };
  }
  return { name: "chat" };
});

/**
 * Recovering a tab that was left open across a deploy.
 *
 * Every route above is lazily imported, so Vite emits a content-hashed chunk per view and the
 * deploy's `aws s3 sync --delete` removes the previous build's. A tab opened before a deploy still
 * holds the old index.html, so navigating to a view it has not downloaded yet requests a chunk that
 * no longer exists. CloudFront answers missing paths with index.html and a 200 -- the deep-link
 * fallback the SPA needs -- so the browser is handed HTML where a module was expected.
 *
 * Vue Router funnels that into onError, which has no default behaviour: the click does nothing at
 * all, with nothing in the UI to explain it. A full load is the fix, because it re-fetches
 * index.html and with it the current chunk hashes.
 */
const RELOAD_MARKER = "cortex:chunk-reload";

function looksLikeStaleChunk(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  // Browsers word this differently: Chrome "Failed to fetch dynamically imported module", Firefox
  // "error loading dynamically imported module", Safari "Importing a module script failed". The
  // parse error is the case where CloudFront's HTML fallback actually reached the JS parser.
  return /dynamically imported module|Importing a module script failed|Unexpected token '<'/i.test(
    message,
  );
}

/** True if a reload may be attempted for this path, recording it so a second failure gives up.
 * Without the marker a genuinely broken build would reload forever instead of surfacing itself. */
function claimReload(path: string): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_MARKER) === path) return false;
    sessionStorage.setItem(RELOAD_MARKER, path);
  } catch {
    // Storage unavailable (private mode, hardened settings). Reloading once is still right; the
    // loop guard is what is lost, and a stale chunk is far more likely than a broken build.
  }
  return true;
}

router.onError((error, to) => {
  // assign() rather than reload(): the navigation was aborted, so the address bar can still be on
  // the previous route.
  if (looksLikeStaleChunk(error) && claimReload(to.fullPath)) window.location.assign(to.fullPath);
});

router.afterEach((to) => {
  // Arrived, so the chunk resolved. Clear the marker or this path could never retry again.
  try {
    if (sessionStorage.getItem(RELOAD_MARKER) === to.fullPath) {
      sessionStorage.removeItem(RELOAD_MARKER);
    }
  } catch {
    // See claimReload.
  }
});

export default router;
